import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { getWhatsAppFlows, sendWhatsAppFlow } from '../../services/api/implementation/impl';
import {
  WA_SEND_CHUNK_SIZE,
  chunkArray,
  formatBulkSendToast,
  summarizeChunkResults
} from '../../utils/bulkWhatsAppSendUtils';

function resolveFlowApprovalStatus(flow) {
  return String(
    flow?.template_approval_status ?? flow?.template_status ?? flow?.approval_status ?? ''
  ).toUpperCase();
}

// A flow can only be sent once its opener template is approved by Meta.
function isFlowSendable(flow) {
  const status = resolveFlowApprovalStatus(flow);
  return status === 'APPROVED' || status === 'ACTIVE';
}

function buildSendPayload({ isPreLeadList, preLeadListId, chunkIds }) {
  if (isPreLeadList) {
    return {
      recipient_type: 'pre_lead_list',
      pre_lead_list_id: Number(preLeadListId),
      pre_lead_ids: chunkIds,
      variables: {},
      skip_template_if_session_open: false
    };
  }
  return {
    recipient_type: 'lead',
    lead_ids: chunkIds,
    variables: {},
    skip_template_if_session_open: false
  };
}

/**
 * Modal that lets the user pick a project's sendable WhatsApp flow and send it
 * via sendWhatsAppFlow.
 *
 * Modes:
 * - recipientType "lead" (default): payload uses lead_ids
 * - recipientType "pre_lead_list": payload uses pre_lead_list_id + optional pre_lead_ids
 *
 * Sends in sequential chunks of WA_SEND_CHUNK_SIZE when N > 50.
 */
const BulkSendFlowModal = ({
  projectId: projectIdProp = null,
  recipientType = 'lead',
  leadIds = [],
  preLeadListId = null,
  preLeadIds = [],
  onClose,
  onSent
}) => {
  const isPreLeadList = recipientType === 'pre_lead_list';
  const recipientIds = isPreLeadList ? preLeadIds : leadIds;
  const recipientLabel = isPreLeadList ? 'prelead' : 'lead';
  const recipientLabelPlural = isPreLeadList ? 'preleads' : 'leads';

  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [pickedProjectId, setPickedProjectId] = useState(
    projectIdProp != null ? String(projectIdProp) : ''
  );
  const [isSending, setIsSending] = useState(false);
  const [sentProgress, setSentProgress] = useState(0);

  const { projectsForDropdown = [], isProjectsLoading } = useProjectsList();

  const projectId = useMemo(() => {
    if (projectIdProp != null && Number(projectIdProp) > 0) return Number(projectIdProp);
    const n = Number(pickedProjectId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [projectIdProp, pickedProjectId]);

  const needsProjectPicker = !(projectIdProp != null && Number(projectIdProp) > 0);

  const {
    data: flows = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['whatsappFlows', projectId],
    queryFn: () => getWhatsAppFlows(projectId),
    enabled: Boolean(projectId)
  });

  const sendableFlows = useMemo(() => flows.filter(isFlowSendable), [flows]);

  const selectedFlow = useMemo(
    () => sendableFlows.find((flow) => String(flow.id) === String(selectedFlowId)) || null,
    [sendableFlows, selectedFlowId]
  );

  const totalRecipients = recipientIds.length;
  const needsChunking = totalRecipients > WA_SEND_CHUNK_SIZE;

  const handleClose = () => {
    if (isSending) return;
    onClose?.();
  };

  const handleSend = async () => {
    if (isSending || recipientIds.length === 0) return;
    if (isPreLeadList && !(Number(preLeadListId) > 0)) {
      toast.error('Missing prelead list.');
      return;
    }
    if (!selectedFlow) {
      toast.error('Please select a flow to send.');
      return;
    }

    const chunks = chunkArray(recipientIds, WA_SEND_CHUNK_SIZE);
    setIsSending(true);
    setSentProgress(0);

    let okTotal = 0;
    let failTotal = 0;
    let processed = 0;
    let hardError = null;

    try {
      for (const chunk of chunks) {
        try {
          const response = await sendWhatsAppFlow(
            selectedFlow.id,
            buildSendPayload({ isPreLeadList, preLeadListId, chunkIds: chunk })
          );
          const { ok, fail } = summarizeChunkResults(response, {
            chunkSize: chunk.length,
            chunkAssumedOk: true
          });
          okTotal += ok;
          failTotal += fail;
        } catch (err) {
          failTotal += chunk.length;
          hardError = err;
          // Continue remaining chunks so partial sends still complete.
        }
        processed += chunk.length;
        setSentProgress(processed);
      }

      const toastMsg = formatBulkSendToast({
        ok: okTotal,
        fail: failTotal,
        total: totalRecipients,
        labelSingular: recipientLabel,
        labelPlural: recipientLabelPlural
      });

      if (failTotal === 0) {
        toast.success(toastMsg);
      } else if (okTotal === 0) {
        const apiMsg =
          hardError?.response?.data?.message || hardError?.message || null;
        toast.error(apiMsg ? `${toastMsg} ${apiMsg}` : toastMsg);
      } else {
        toast.error(toastMsg);
      }

      onSent?.();
      onClose?.();
    } finally {
      setIsSending(false);
      setSentProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Send a flow</p>
            <p className="truncate text-xs text-gray-500">
              To {totalRecipients} selected{' '}
              {totalRecipients > 1 ? recipientLabelPlural : recipientLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            aria-label="Close">
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {needsProjectPicker ? (
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                Select a project
              </label>
              {isProjectsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <select
                  value={pickedProjectId}
                  onChange={(e) => {
                    setPickedProjectId(e.target.value);
                    setSelectedFlowId('');
                  }}
                  disabled={isSending}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                  <option value="" disabled>
                    Choose a project…
                  </option>
                  {projectsForDropdown.map((project) => (
                    <option key={project.value ?? project.id} value={project.value ?? project.id}>
                      {project.label ?? project.name ?? `Project ${project.value ?? project.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : null}

          {!projectId ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {needsProjectPicker
                ? `Select a project to load WhatsApp flows for these ${recipientLabelPlural}.`
                : `These ${recipientLabelPlural} aren’t linked to a single project, so flows can’t be listed.`}
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <p className="text-sm text-gray-600">Couldn’t load flows.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2A565E]/90">
                Try again
              </button>
            </div>
          ) : sendableFlows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No approved flows available for this project. Only flows whose opener template is
              approved by Meta can be sent.
            </p>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                  Select a flow
                </label>
                <select
                  value={selectedFlowId}
                  onChange={(e) => setSelectedFlowId(e.target.value)}
                  disabled={isSending}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                  <option value="" disabled>
                    Choose a flow…
                  </option>
                  {sendableFlows.map((flow) => (
                    <option key={flow.id} value={flow.id}>
                      {flow.name || 'Untitled flow'}
                    </option>
                  ))}
                </select>

                {selectedFlow?.description ? (
                  <p className="mt-2 text-xs text-gray-400">{selectedFlow.description}</p>
                ) : null}
              </div>

              {isSending ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">
                    Sent {sentProgress}/{totalRecipients}…
                  </p>
                  <p className="mt-1 text-xs text-amber-800">
                    Keep this page open until sending finishes
                  </p>
                  {needsChunking ? (
                    <p className="mt-1 text-[11px] text-amber-700">
                      Sending in batches of {WA_SEND_CHUNK_SIZE}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || !selectedFlow}
                  className="flex items-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2A565E]/90 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2} />
                  )}
                  {isSending ? `Sent ${sentProgress}/${totalRecipients}` : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkSendFlowModal;
